import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * Normalizes URLs for SEO canonical consistency:
 *  1. Strips trailing slashes (except root "/") to avoid duplicate-content
 *     pairs like /foo and /foo/.
 *  2. Strips known marketing/referral params (ref) that create duplicate
 *     URLs in Google's index without semantic value. UTM params are kept
 *     because analytics needs them — those are handled at the canonical
 *     <link> level by SEOHead.
 *
 * Uses a ref guard to prevent infinite loops with history.replaceState().
 */
const PARAMS_TO_STRIP = ["ref", "fbclid", "gclid_dup"];

export const TrailingSlashRedirect = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const lastProcessedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const { pathname, search, hash } = location;

    // Build a key that captures both pathname AND tracked-param state so we
    // re-evaluate when either changes (but don't loop on our own rewrites).
    const key = `${pathname}|${search}`;
    if (lastProcessedKeyRef.current === key) return;

    let nextPath = pathname;
    let nextSearch = search;
    let changed = false;

    // 1) Trailing slash normalization (skip root)
    if (nextPath.length > 1 && nextPath.endsWith("/")) {
      nextPath = nextPath.slice(0, -1);
      changed = true;
    }

    // 2) Strip non-canonical marketing params
    if (nextSearch) {
      const params = new URLSearchParams(nextSearch);
      let stripped = false;
      for (const p of PARAMS_TO_STRIP) {
        if (params.has(p)) {
          params.delete(p);
          stripped = true;
        }
      }
      if (stripped) {
        const cleaned = params.toString();
        nextSearch = cleaned ? `?${cleaned}` : "";
        changed = true;
      }
    }

    if (changed) {
      lastProcessedKeyRef.current = `${nextPath}|${nextSearch}`;
      navigate(`${nextPath}${nextSearch}${hash}`, { replace: true });
    } else {
      lastProcessedKeyRef.current = key;
    }
  }, [location.pathname, location.search]);

  return null;
};
